import { useEffect, useRef, useState } from "react";

interface Options {
  /**
   * How far outside the viewport the element counts as "approaching".
   * Generous by default so embeds finish loading before they scroll into
   * view and the visitor never sees a placeholder.
   */
  rootMargin?: string;
}

/**
 * Tracks whether an element has come within `rootMargin` of the viewport.
 * Latches to true on the first hit and disconnects — used to hold heavy
 * third-party iframes and map tiles back until they are actually approaching.
 *
 * Falls back to true immediately where IntersectionObserver is unavailable,
 * so the content is never withheld from a browser that cannot report it.
 */
export function useInViewport<T extends HTMLElement>({ rootMargin = "600px" }: Options = {}) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}
